'use client';

import { useEffect, useRef, useState } from 'react';
import DailyIframe, { DailyCall } from '@daily-co/daily-js';
import { Button } from '@/components/ui/button';
import { Loader2, Mic, MicOff, Video, VideoOff, Monitor, MonitorOff } from 'lucide-react';

interface DailyVideoPlayerProps {
  roomUrl: string;
  token: string;
  userName: string;
  onLeave?: () => void;
}

export function DailyVideoPlayer({ roomUrl, token, userName, onLeave }: DailyVideoPlayerProps) {
  const callFrameRef = useRef<DailyCall | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isJoined, setIsJoined] = useState(false);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);

  useEffect(() => {
    if (!containerRef.current || !roomUrl || !token) return;
    if (callFrameRef.current) return;

    let callFrame: DailyCall | null = null;
    let isActive = true;

    const initializeCall = async () => {
      try {
        if (!isActive) return;

        const cleanRoomUrl = roomUrl.trim().replace(/[\x00-\x1F\x7F]/g, '');

        console.log('Original room URL:', roomUrl);
        console.log('Clean room URL:', cleanRoomUrl);
        console.log('Room URL bytes:', Array.from(roomUrl).map((c) => `${c}(${c.charCodeAt(0)})`).join(' '));

        if (!cleanRoomUrl.startsWith('https://')) {
          throw new Error('Invalid room URL format');
        }

        callFrame = DailyIframe.createFrame(containerRef.current!, {
          showLeaveButton: false,
          showFullscreenButton: true,
          iframeStyle: {
            width: '100%',
            height: '100%',
            border: '0',
            borderRadius: '0.5rem',
          },
        });

        if (!isActive) {
          callFrame.destroy();
          return;
        }

        callFrameRef.current = callFrame;

        callFrame.on('loading', (event) => {
          console.log('Daily.co loading event:', event);
        });

        callFrame.on('loaded', (event) => {
          console.log('Daily.co loaded event:', event);
          setIsLoading(false);
        });

        callFrame.on('joining-meeting', (event) => {
          console.log('Daily.co joining-meeting event:', event);
        });

        callFrame.on('joined-meeting', (event) => {
          console.log('Successfully joined meeting:', event);
          setIsJoined(true);
        });

        callFrame.on('left-meeting', (event) => {
          console.log('Left meeting:', event);
          setIsJoined(false);
          if (onLeave) onLeave();
        });

        callFrame.on('error', (event) => {
          console.error('Daily.co error event:', event);
          setIsLoading(false);
        });

        callFrame.on('camera-error', (event) => {
          console.error('Daily.co camera error:', event);
        });

        callFrame.on('participant-joined', (event) => {
          console.log('Participant joined:', event);
        });

        console.log('Joining room with clean URL:', cleanRoomUrl);
        console.log('Token (first 20 chars):', token.substring(0, 20) + '...');
        console.log('Username:', userName);

        const joinResult = await callFrame.join({
          url: cleanRoomUrl,
          token: token,
          userName: userName,
        });

        console.log('Join result:', joinResult);
      } catch (error) {
        console.error('Error initializing Daily call:', error);
        setIsLoading(false);
      }
    };

    initializeCall();

    return () => {
      isActive = false;
      if (callFrameRef.current) {
        callFrameRef.current.destroy().catch((error) => {
          console.error('Error destroying Daily call:', error);
        });
        callFrameRef.current = null;
      }
    };
  }, [roomUrl, token, userName]);

  const toggleMic = async () => {
    if (!callFrameRef.current) return;
    const newState = !isMicOn;
    await callFrameRef.current.setLocalAudio(newState);
    setIsMicOn(newState);
  };

  const toggleCamera = async () => {
    if (!callFrameRef.current) return;
    const newState = !isCameraOn;
    await callFrameRef.current.setLocalVideo(newState);
    setIsCameraOn(newState);
  };

  const toggleScreenShare = async () => {
    if (!callFrameRef.current) return;

    if (isScreenSharing) {
      await callFrameRef.current.stopScreenShare();
      setIsScreenSharing(false);
    } else {
      await callFrameRef.current.startScreenShare();
      setIsScreenSharing(true);
    }
  };

  const handleLeave = async () => {
    if (callFrameRef.current) {
      await callFrameRef.current.leave();
    }
  };

  return (
    <div className="space-y-4">
      <div
        ref={containerRef}
        className="relative w-full bg-slate-900 rounded-lg overflow-hidden"
        style={{ height: '600px' }}
      >
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
            <div className="text-center">
              <Loader2 className="h-12 w-12 animate-spin text-blue-500 mx-auto mb-4" />
              <p className="text-white">Connecting to live session...</p>
            </div>
          </div>
        )}
      </div>

      {isJoined && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant={isMicOn ? 'default' : 'destructive'}
            size="lg"
            onClick={toggleMic}
          >
            {isMicOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
          </Button>

          <Button
            variant={isCameraOn ? 'default' : 'destructive'}
            size="lg"
            onClick={toggleCamera}
          >
            {isCameraOn ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
          </Button>

          <Button
            variant={isScreenSharing ? 'secondary' : 'default'}
            size="lg"
            onClick={toggleScreenShare}
          >
            {isScreenSharing ? <MonitorOff className="h-5 w-5" /> : <Monitor className="h-5 w-5" />}
          </Button>

          <Button
            variant="destructive"
            size="lg"
            onClick={handleLeave}
          >
            Leave Session
          </Button>
        </div>
      )}
    </div>
  );
}
